import React from "react"

import styles from "./Iframe.module.scss"

export interface IframeProps {
	src?: string
	alt?: string
	iframeContainerProp?: string
}

const Iframe: React.FC<IframeProps> = ({src, alt, iframeContainerProp}) => {
	const containerRef = React.useRef<HTMLDivElement | null>(null)

	const site = "https://dreambroker.com"

	const extractResource = (src: string): string | null => {
		try {
			const url = new URL(src, site)
			const match = url.pathname.match(
				/^\/channel\/([a-zA-Z0-9]+)\/iframe\/([a-zA-Z0-9]+)/,
			)
			if (match) {
				const [, channelId, videoId] = match
				return `${channelId}/${videoId}`
			}
			return null
		} catch {
			console.warn("URL parsing failed, using fallback iframe for:", src)
			return null
		}
	}

	const extractParams = (
		src: string,
	): {
		quality?: string | null
		controls?: boolean
		subtitlelang?: string | null
		branding?: boolean
		brandingcolor?: string | null
	} => {
		try {
			const url = new URL(src, site)
			const quality = url.searchParams.get("quality")
			const controls = url.searchParams.get("controls") !== "false"
			const subtitlelang = url.searchParams.get("lang")
			const branding = url.searchParams.get("branding") === "true"
			const brandingcolor = url.searchParams.get("brandingcolor")

			return {quality, controls, subtitlelang, branding, brandingcolor}
		} catch (error) {
			return {}
		}
	}

	const renderCustomPlayer = (
		resource: string | null,
		text: string,
		quality?: string | null,
		controls?: boolean | null,
		subtitlelang?: string | null,
		branding?: boolean | null,
		brandingcolor?: string | null,
	): boolean => {
		if (!resource) return false

		if (
			"customElements" in window &&
			customElements.get("dreambroker-studio-video-player")
		) {
			const iframe = document.createElement(
				"dreambroker-studio-video-player",
			)
			// @ts-expect-error
			iframe.site = site
			// @ts-expect-error
			iframe.resource = resource
			iframe.title = text
			iframe.className = styles.iframe
			// @ts-expect-error
			iframe.width = "-1"
			// @ts-expect-error
			iframe.height = "-1"
			// @ts-expect-error
			iframe.quality = quality
			// @ts-expect-error
			iframe.controls = controls !== false
			// @ts-expect-error
			iframe.subtitlelang = subtitlelang
			// @ts-expect-error
			iframe.branding = branding
			// @ts-expect-error
			iframe.brandingcolor = brandingcolor
			containerRef.current?.appendChild(iframe)

			return true
		}
		return false
	}

	const renderFallbackToNativeIframe = (src: string, text: string) => {
		const fallbackIframe = document.createElement("iframe")
		fallbackIframe.src = src
		fallbackIframe.title = text
		fallbackIframe.className = styles.iframe
		fallbackIframe.frameBorder = "0"
		fallbackIframe.allowFullscreen = true
		containerRef.current?.appendChild(fallbackIframe)
	}

	React.useEffect(() => {
		if (!containerRef.current || !src || !alt) return

		containerRef.current.innerHTML = ""

		const resource = extractResource(src)
		const {quality, controls, subtitlelang, branding, brandingcolor} =
			extractParams(src)
		const useCustomPlayer = Boolean(resource)

		const text = alt
			.replace(/<\/?(p|h[1-6]|br|div)[^>]*>/gi, " ")
			.replace(/<[^>]+>/g, "")
			.replace(/&[^;]+;/g, (m) => (m === "&amp;" ? "&" : ""))
			.replace(/[\r\n]+/g, " ")
			.replace(/\s{2,}/g, " ")
			.trim()

		if (useCustomPlayer) {
			const isRenderCustomePlayerSuccessful = renderCustomPlayer(
				resource,
				text,
				quality,
				controls,
				subtitlelang,
				branding,
				brandingcolor,
			)

			if (
				!isRenderCustomePlayerSuccessful &&
				"customElements" in window
			) {
				renderFallbackToNativeIframe(src, text)

				customElements
					.whenDefined("dreambroker-studio-video-player")
					.then(() => {
						if (containerRef.current) {
							containerRef.current.innerHTML = ""
							renderCustomPlayer(
								resource,
								text,
								quality,
								controls,
								subtitlelang,
								branding,
								brandingcolor,
							)
						}
					})
					.catch(() => {
						// never resolves, native iframe remains
					})
			}
		} else {
			renderFallbackToNativeIframe(src, text)
		}

		return () => {
			if (containerRef.current) {
				containerRef.current.innerHTML = ""
			}
		}
	}, [src, alt])

	return (
		<div
			ref={containerRef}
			className={`${styles.iframeContainer} ${iframeContainerProp}`}
		/>
	)
}

export default Iframe
